import { disableBodyScroll } from 'body-scroll-lock';
import classNames from 'classnames';
import { ReactElement, ReactNode, useEffect, useRef } from 'react';

import styles from './modalOverlay.module.css';

import { Typography } from '@/shared/components/atoms/Typography';
import { TypographyVariant } from '@/shared/components/atoms/Typography/Typography.types';
import { clsxm } from '@/shared/lib';

import CloseIcon from '~/images/shared/CloseWithoutBG.svg';
import CloseCircular from '~/images/shared/Line.svg';

type ModalOverlayProps = {
  children: ReactNode;
  closeModal: () => void;
  open: boolean;
  footer?: ReactNode;
  modalPosition?: 'center' | 'left' | 'right';
  className?: string;
  containerClassName?: string;
  dismissable?: boolean;
  title?: string | ReactNode;
  full?: boolean;
  titleVariant?: TypographyVariant;
};

export default function ModalOverlay({
  children,
  modalPosition = 'center',
  closeModal,
  className,
  open,
  title = '',
  footer,
  containerClassName,
  dismissable = true,
  full,
  titleVariant,
}: ModalOverlayProps): ReactElement {
  const activeModalPosition = {
    center: 'justify-center items-center',
    left: 'justify-start',
    right: 'justify-end',
  };

  const overlayClasses = clsxm(
    styles.overlay,
    activeModalPosition[modalPosition],
    className,
    open ? 'flex' : 'hidden'
  );

  const containerClasses = clsxm(
    'modalOverlay',
    'rounded-xl bg-white px-24 overflow-y-auto',
    full
      ? 'h-[100dvh] w-screen py-24 flex flex-col'
      : 'tablet:max-h-[100dvh] py-16 w-[90vw] max-h-[90dvh] tablet:w-auto scrollbar',
    containerClassName
  );

  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targetElement = targetRef.current;
    if (targetElement) {
      disableBodyScroll(targetElement);
    }
  }, []);

  return open ? (
    <div
      tabIndex={0}
      onClick={dismissable ? () => closeModal() : undefined}
      className={overlayClasses}
    >
      {/* body */}
      <div onClick={(e) => e.stopPropagation()}>
        <div ref={targetRef} className={containerClasses}>
          {/* header */}
          {(dismissable || title) && (
            <div
              className={classNames(
                'flex w-full items-start justify-between pb-16',
                full && 'border-collapse border-b-xs border-neutralVariant-90'
              )}
            >
              {typeof title === 'string' ? (
                <Typography
                  variant={titleVariant ? titleVariant : 'body1'}
                  weight='bold'
                >
                  {title}
                </Typography>
              ) : (
                title
              )}

              {dismissable && (
                <>
                  <span
                    className='h-24 w-24 cursor-pointer text-24 text-primary-default'
                    onClick={() => closeModal()}
                  >
                    {full ? (
                      <CloseIcon width='32' height='32' />
                    ) : (
                      <CloseCircular />
                    )}
                  </span>
                  <span className='sr-only'>Close modal</span>
                </>
              )}
            </div>
          )}
          {/* children */}
          <div className={clsxm(full && 'w-full flex-grow')}>{children}</div>
          {/* footer */}
          {footer && <div className=''>{footer || ''}</div>}
        </div>
      </div>
    </div>
  ) : (
    <span className='hidden'></span>
  );
}
